import type { DataPointSnapResult, SnapStrategyContext } from '../types';

import { BaseSnapStrategy } from './BaseSnapStrategy';

/**
 * Snap strategy for line charts.
 *
 * Uses point-based snapping:
 * - Snaps to data points that are within snapDistance of the cursor
 * - Shows crosshair on both data points and reference lines
 */
export class LineChartSnapStrategy extends BaseSnapStrategy {
  constructor() {
    super(0); // No bandwidth for line charts
  }

  findClosestDataPoint(context: SnapStrategyContext): DataPointSnapResult | null {
    const { cursorSvgX, chartData, xDataKey, xScale, snapDistance } = context;

    let result: DataPointSnapResult | null = null;

    for (const datum of chartData) {
      const xValue = datum[xDataKey];
      if (typeof xValue !== 'number') {
        continue;
      }

      const scaleInput = this.getScaleInputForValue(xValue, context);
      const scaledX = xScale(scaleInput as number);
      if (typeof scaledX !== 'number') {
        continue;
      }

      // For line charts, use distance to the point
      const distance = Math.abs(scaledX - cursorSvgX);
      if (distance <= snapDistance && (!result || distance < result.distance)) {
        result = { xValue, xPosition: scaledX, distance };
      }
    }

    return result;
  }

  shouldShowCrosshair(_hasDataPoint: boolean, _hasNearbyReferenceLine: boolean): boolean {
    // Line charts always show crosshair when snapped to data point or reference line
    return true;
  }
}
